import {CA} from '../../analytics/Analytics.ts'
import {type UIGameManager} from '../UIGameManager.ts'
import {GameTypeEnum, type MatchmakingMatchTypes} from '@colonist/game-base-types'
import {UIEntryUrlParameterController} from '../../UIEntryUrlParameterController.ts'
import {ViewPopupMembershipIncentiveEndgame} from '../../ui_popup/ui_popup_membership_incentive/ViewPopupMembershipIncentiveEndgame.ts'
import {UIUrlParameterNames} from '../../ui_html/UIUrlController.ts'
import {socket} from '../../../../shared/scripts/EntryShared.ts'
import {type TranslationObject} from '@colonist/i18n'
import {UserSessionUtils} from '../../UserSessionUtils.ts'
import {UIUtilsEjsVariables} from '../../UIUtilsEjsVariables.ts'
import {UIFeedbackFormPopupController} from '../../ui_popup/ui_popup_feedback_form/UIFeedbackFormPopupController.ts'
import {AnalyticEventType, FeedbackFormLocationEnum} from '../../../../../shared/entity_datas/EntityDatas.ts'
import {FeedbackFormUtils} from '../../ui_popup/ui_popup_feedback_form/FeedbackFormUtils.ts'
import {ViewPopupChangeOnlineUsername} from '../../ui_popup/ViewPopupChangeOnlineUsername.ts'
import {StorageWrapper} from '../../StorageWrapper.ts'
import {getHomepageUrl, getHomepageUrlWithParameter, navigateToPage} from '../../ui_utils/ClientUtils.ts'
import {ViewPopupKeepPlayingOnWeb} from '../../ui_popup/ViewPopupKeepPlayingOnWeb.ts'
import {DurationConverter, TimeUtils} from '@colonist/utils'

export class UIGameEndFooterController {
    private readonly uiGameManager: UIGameManager

    private readonly REMATCH_TEXT: TranslationObject = {key: 'strings:game.endGame.footer.rematch'}
    private readonly CONTINUE_TEXT: TranslationObject = {key: 'strings:game.endGame.footer.continue'}
    private readonly HOME_TEXT: TranslationObject = {key: 'strings:game.endGame.footer.home'}
    private readonly PLAY_TEXT: TranslationObject = {key: 'strings:game.endGame.footer.play'}

    private isFirstClickOnBackToLobby: boolean
    private isFirstClickOnRematch: boolean
    private isFirstClickOnPlayAgain: boolean

    constructor(uiGameManager: UIGameManager) {
        this.uiGameManager = uiGameManager

        this.isFirstClickOnBackToLobby = true
        this.isFirstClickOnRematch = true
        this.isFirstClickOnPlayAgain = true
    }

    onClickRematch() {
        void this.showPopupsBeforeExitingGame(() => {
            CA.sendGoogleAnalyticsEvent(CA.Category.EndGame, CA.Label.ClickedEndGameRematch)
            const isSocketOpen = socket?.controller.isSocketOpen()
            if(isSocketOpen) {
                this.uiGameManager.socketGameSend.clickedRematch()
                return
            }
            window.location.hash = socket.gameListener.databaseGameId
            window.location.reload()
        }, this.isFirstClickOnRematch, this.REMATCH_TEXT)
        this.isFirstClickOnRematch = false
    }

    onClickHome() {
        this.handleExitingGame()
    }

    onClickPlayAgain() {
        void this.showPopupsBeforeExitingGame(() => {
            const matchmakingMatchType = this.uiGameManager.gameController.gameDetails.matchmakingMatchType
            if(matchmakingMatchType !== undefined) return UIGameEndFooterController.sendDirectlyToMatchmakingQueue(matchmakingMatchType)

            const gameType = this.uiGameManager.gameController.gameSettings.gameType
            if(gameType === GameTypeEnum.BotGame) return UIGameEndFooterController.sendDirectlyToBotGame()

            UIGameEndFooterController.sendToMainPage()
        }, this.isFirstClickOnPlayAgain, this.PLAY_TEXT)
        this.isFirstClickOnPlayAgain = false
    }

    async onClickNextLevelBeginnerMode() {
        CA.sendGoogleAnalyticsEvent(CA.Category.EndGame, CA.Label.ClickedEndGameRematch)
        await this.uiGameManager.socketGameSend.sendGameAnalyticOnce(AnalyticEventType.BeginnerModeClickedNextLevel, StorageWrapper.getBeginnerModeLevel())
        UIGameEndFooterController.sendToNextCampaignLevel()
    }

    async onClickRetryBeginnerMode() {
        StorageWrapper.decrementBeginnerModeLevel()
        await this.uiGameManager.socketGameSend.sendGameAnalyticOnce(AnalyticEventType.BeginnerModeClickedRetry, StorageWrapper.getBeginnerModeLevel())
        UIGameEndFooterController.sendToNextCampaignLevel()
    }

    private static sendToLobby() {
        const redirectUrl = UIGameEndFooterController.shouldShowShareActivityLinkPopup() ? getHomepageUrlWithParameter(UIUrlParameterNames.DiscordShareActivity, UIEntryUrlParameterController.DEFAULT_PARAMETER_VALUE) : getHomepageUrl()

        window.history.pushState('', document.title, redirectUrl)
        location.reload()
    }

    private static sendDirectlyToMatchmakingQueue(matchType: MatchmakingMatchTypes) {
        navigateToPage(getHomepageUrlWithParameter(UIUrlParameterNames.EnterMatchmaking, matchType))
    }

    private static sendDirectlyToBotGame() {
        navigateToPage(getHomepageUrlWithParameter(UIUrlParameterNames.EnterBotGame, UIEntryUrlParameterController.DEFAULT_PARAMETER_VALUE))
    }

    private static sendToMainPage() {
        navigateToPage(getHomepageUrlWithParameter(UIUrlParameterNames.MainPage, UIEntryUrlParameterController.DEFAULT_PARAMETER_VALUE))
    }

    private static sendToNextCampaignLevel() {
        navigateToPage(getHomepageUrlWithParameter(UIUrlParameterNames.Campaign, UIEntryUrlParameterController.DEFAULT_PARAMETER_VALUE))
    }

    handleExitingGame() {
        void this.showPopupsBeforeExitingGame(() => {
            CA.sendGoogleAnalyticsEvent(CA.Category.EndGame, CA.Label.ClickedEndGameLobby)
            UIGameEndFooterController.sendToLobby()
        }, this.isFirstClickOnBackToLobby, this.HOME_TEXT)
        this.isFirstClickOnBackToLobby = false
    }

    private async showPopupsBeforeExitingGame(continuationAction: () => void, isFirstClick: boolean, continuationText: TranslationObject = this.CONTINUE_TEXT) {
        if(!isFirstClick) return continuationAction()

        const shouldShowDiscordKeepPlayingOnWebPopup = UserSessionUtils.canShowDiscordKeepPlayingOnWebPopup()
        if(shouldShowDiscordKeepPlayingOnWebPopup) {
            new ViewPopupKeepPlayingOnWeb(continuationAction)
            StorageWrapper.setDiscordKeepPlayingOnWebPopupShown()
            return
        }

        const shouldShowChangeUsernamePopup = UserSessionUtils.shouldShowChangeUsernamePopup(this.uiGameManager.gameController.gameDetails.matchmakingMatchType)
        if(shouldShowChangeUsernamePopup) {
            new ViewPopupChangeOnlineUsername(continuationAction)
            StorageWrapper.setDiscordOnlineUsernamePopupShown()
            return
        }

        const shouldShowFeedbackForm = FeedbackFormUtils.shouldShowFeedbackForm()
        if(shouldShowFeedbackForm) {
            new UIFeedbackFormPopupController(() => continuationAction(), socket.gameListener.databaseGameId, FeedbackFormLocationEnum.EndGame)
            return
        }

        const shouldShowMembershipIncentive = UserSessionUtils.canShowSubscriptionPopup()
        if(shouldShowMembershipIncentive) {
            new ViewPopupMembershipIncentiveEndgame(continuationAction, this.uiGameManager.gameController.gameSettings, continuationText)
            return
        }

        continuationAction()
    }

    private static shouldShowShareActivityLinkPopup(): boolean {
        if(!UIUtilsEjsVariables.isUserOnDiscord) return false

        const lastMembershipIncentivePopupTime = StorageWrapper.getLastMembershipIncentivePopupTime()
        if(!UIGameEndFooterController.popupWasShownInLastTwentyFourHours(lastMembershipIncentivePopupTime)) return false

        const lastShareActivityLinkPopupTime = StorageWrapper.getLastShareActivityLinkPopupTime()
        if(UIGameEndFooterController.popupWasShownInLastTwentyFourHours(lastShareActivityLinkPopupTime)) return false

        return true
    }

    static popupWasShownInLastTwentyFourHours(lastPopupTime: Date | null): boolean {
        if(lastPopupTime == null) return false
        const millisecondsSinceLastPopupTime = TimeUtils.millisecondsPassedSince(lastPopupTime.getTime())
        const shownWithinLastDay = millisecondsSinceLastPopupTime < DurationConverter.daysAsMilliseconds(1)
        return shownWithinLastDay
    }
}
